import { Flex, Text } from '@byecode/ui'
import type { ActorEvent, RecordLikeProtocol, SizeConfigure, VariableADTvalue, ViewStyles } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import {
    fieldConvertValue,
    getBackgroundStyle,
    getRecordPoolKey,
    getTextColorStyle,
    useAppThemeContext,
    useAtomData,
    useFlowLayoutContext
} from '@lighthouse/shared'
import {
    addDays,
    eachDayOfInterval,
    eachWeekOfInterval,
    endOfMonth,
    isSameDay,
    isSameMonth,
    lightFormat,
    startOfMonth
} from 'date-fns'
import React, { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context/ViewBlockContext'
import { Item } from '../CustomViewBlock/Item'
import { setDeadCopyNode } from '../CustomViewBlock/utils'
import type { SpannerSegment } from './utils'
import { computeWeekSpanners } from './utils'

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

const CalendarGrid = styled.div<{ showBorder?: boolean; borderColor?: string }>`
    ${({ showBorder, borderColor }) => `
        --border-size: ${showBorder ? 1 : 0}px;
        --border-color: ${borderColor || ''};
    `};
    flex: 1;
    overflow: auto hidden;
    border-top: var(--border-size) solid var(--border-color);
    border-left: var(--border-size) solid var(--border-color);
`

const WeekHeaderRow = styled.div<{ itemWidth?: number }>`
    flex-shrink: 0;
    display: grid;
    grid-template-columns: ${({ itemWidth }) =>
        itemWidth ? `repeat(7, minmax(${itemWidth}px, 1fr))` : 'repeat(7, minmax(0, 1fr))'};
`

const WeekHeaderCell = styled.div`
    padding: 6px 8px;
    font-size: 13px;
    font-weight: 400;
    text-align: center;
    color: var(--color-gray-400);
    border-right: var(--border-size) solid var(--border-color);
    border-bottom: var(--border-size) solid var(--border-color);
`

const WeekGrid = styled.div<{ itemWidth?: number; uniformRowHeight?: boolean; squareCells?: boolean; contentRow?: number }>`
    display: grid;
    grid-template-columns: ${({ itemWidth }) =>
        itemWidth ? `repeat(7, minmax(${itemWidth}px, 1fr))` : 'repeat(7, minmax(0, 1fr))'};
    ${({ squareCells, uniformRowHeight, contentRow = 2 }) =>
        squareCells
            ? 'grid-template-rows: auto auto;'
            : uniformRowHeight
              ? `grid-template-rows: repeat(${contentRow - 1}, auto) 1fr;`
              : 'grid-auto-rows: auto;'}
`

const DateCell = styled.div`
    padding: 4px 8px 2px;
    border-right: var(--border-size) solid var(--border-color);
`

const SpannerCell = styled.div`
    padding: 1px 8px;
    min-width: 0;
    overflow: hidden;
    border-right: var(--border-size) solid var(--border-color);
`

/**
 * 在横跨行中、未被 SpannerCell 占据的列里放置这个空 div，
 * 依靠 align-self: stretch（CSS Grid 默认值）撑到整行高度，
 * 从而让 border-right 保持与上下行对齐，边框不再中断。
 */
const SpannerBorderCell = styled.div`
    border-right: var(--border-size) solid var(--border-color);
`

const ContentCell = styled.div`
    display: flex;
    flex-direction: column;
    border-right: var(--border-size) solid var(--border-color);
    border-bottom: var(--border-size) solid var(--border-color);
`

const DayNumber = styled(Text)<{ isCurrentMonth?: boolean }>`
    margin-bottom: 2px;
    font-size: 12px;
    opacity: ${({ isCurrentMonth }) => (isCurrentMonth ? 1 : 0.35)};
`

// Week starts on Monday (hardcoded for month calendar)
const WEEK_STARTS_ON = 1 as const
// Labels in Mon-Sun order
const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日']

interface MonthCalendarViewProps {
    id?: string
    currentMonth: Date
    node: FlowLayoutNode
    title: string
    gap: number
    readonly?: boolean
    itemWidth?: number
    calendarStartDateField?: string
    calendarEndDateField?: string
    viewStyles?: ViewStyles
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

export const MonthCalendarView = memo((props: MonthCalendarViewProps) => {
    const {
        id,
        currentMonth,
        node,
        title,
        gap,
        readonly,
        itemWidth,
        calendarStartDateField,
        calendarEndDateField,
        viewStyles,
        onRecordClick
    } = props

    const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
    const blockId = blockData.id

    const monthStart = useMemo(() => startOfMonth(currentMonth), [currentMonth])
    const monthEnd = useMemo(() => endOfMonth(currentMonth), [currentMonth])

    const calendarWeeks = useMemo(() => {
        return eachWeekOfInterval({ start: monthStart, end: monthEnd }, { weekStartsOn: WEEK_STARTS_ON }).map(weekStart =>
            eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })
        )
    }, [monthEnd, monthStart])

    const records = useAtomData(
        recordPoolAtom,
        useCallback(
            pool => {
                if (!calendarStartDateField || !calendarEndDateField) {
                    return []
                }
                const startFieldDef = dataSource.schema[calendarStartDateField]
                const endFieldDef = dataSource.schema[calendarEndDateField]
                if (!startFieldDef || startFieldDef.innerType !== 'DATE' || !endFieldDef || endFieldDef.innerType !== 'DATE') {
                    return []
                }
                return recordPoints
                    .map(p => pool.get(getRecordPoolKey(dataSource.id, p.id)))
                    .filter((item): item is RecordLikeProtocol => !!item)
            },
            [calendarStartDateField, calendarEndDateField, dataSource.id, dataSource.schema, recordPoints]
        )
    )

    // Single editable template container: the record with the earliest start date
    const editableRecordId = useMemo(() => {
        if (!calendarStartDateField) {
            return undefined
        }
        return records.reduce<string | undefined>((prevId, curr) => {
            const currVal = curr.content[calendarStartDateField]?.value
            if (typeof currVal !== 'number') {
                return prevId
            }
            if (!prevId) {
                return curr.id
            }
            const prevRecord = records.find(r => r.id === prevId)
            const prevVal = prevRecord?.content[calendarStartDateField]?.value
            return typeof prevVal === 'number' && currVal < prevVal ? curr.id : prevId
        }, undefined)
    }, [calendarStartDateField, records])

    // Per-week spanning record segments
    const weekSpanners = useMemo(() => {
        if (!calendarStartDateField || !calendarEndDateField) {
            return calendarWeeks.map(() => [] as SpannerSegment[])
        }
        return calendarWeeks.map(week => computeWeekSpanners(records, calendarStartDateField, calendarEndDateField, week))
    }, [records, calendarStartDateField, calendarEndDateField, calendarWeeks])

    const {
        theme: { palettes }
    } = useAppThemeContext()

    const { useParseVariableWithSubscription } = useFlowLayoutContext()
    const parseTrBackgroundColorVariable = useParseVariableWithSubscription(viewStyles?.calendarDateRow?.background?.variable)

    const dateRowBackgroundStyle = getBackgroundStyle({
        value: viewStyles?.calendarDateRow?.background,
        palettes,
        parseVariableColor: (v: VariableADTvalue) => {
            const field = parseTrBackgroundColorVariable(v)
            return field ? fieldConvertValue(field) : ''
        }
    })

    const minHeightConfigure = useMemo(() => node.children?.length
        ? {}
        : ({
              minHeight: {
                  size: 80,
                  unit: 'px'
              } as const
          })
    , [node.children?.length])

    const uniformRowHeight = viewStyles?.uniformRowHeight ?? false

    const getSingleDayRecords = useCallback(
        (day: Date) => {
            if (!calendarStartDateField || !calendarEndDateField) {
                return []
            }
            return records.filter(r => {
                const startVal = r.content[calendarStartDateField]?.value
                const endVal = r.content[calendarEndDateField]?.value
                if (typeof startVal !== 'number' || typeof endVal !== 'number') {
                    return false
                }
                return isSameDay(startVal, day) && isSameDay(startVal, endVal)
            })
        },
        [calendarStartDateField, calendarEndDateField, records]
    )

    const hasVisibleRecords = useMemo(
        () =>
            weekSpanners.some(spanners => spanners.length > 0) ||
            calendarWeeks.some(week => week.some(day => getSingleDayRecords(day).length > 0)),
        [calendarWeeks, getSingleDayRecords, weekSpanners]
    )
    const squareCells = uniformRowHeight && !hasVisibleRecords

    // Refs for each rendered WeekGrid, used to equalize heights across weeks.
    const weekGridRefs = useRef<(HTMLDivElement | null)[]>([])

    useLayoutEffect(() => {
        const refs = weekGridRefs.current.filter(Boolean) as HTMLDivElement[]
        if (refs.length === 0) return

        if (!uniformRowHeight || squareCells) {
            // Reset any previously applied min-height so weeks shrink back to content size.
            refs.forEach(ref => { ref.style.minHeight = '' })
            return
        }

        let appliedMax = 0

        // Clear, measure natural content heights, then apply the tallest as a
        // min-height (NOT height) to every grid. min-height lets a grid keep
        // growing when its content outgrows the applied value — which is the
        // only signal ResizeObserver can pick up after the initial measure.
        const equalize = () => {
            refs.forEach(ref => { ref.style.minHeight = '' })
            const naturalMax = Math.max(...refs.map(ref => ref.offsetHeight))
            if (naturalMax > 0) {
                appliedMax = naturalMax
                refs.forEach(ref => { ref.style.minHeight = `${naturalMax}px` })
            }
        }

        // Re-equalize ONLY when some grid's content has actually outgrown the
        // applied max. Reading offsetHeight without clearing is safe because
        // offsetHeight = max(minHeight, content): a grid whose content exceeds
        // appliedMax reports a value above appliedMax, while grids still under
        // it report exactly appliedMax. When nothing outgrows it we leave the
        // DOM untouched — that's what breaks the equalize feedback loop.
        const check = () => {
            const currentMax = Math.max(...refs.map(ref => ref.offsetHeight))
            if (currentMax > appliedMax) {
                equalize()
            }
        }

        // Initial equalize runs before paint, so there's no flash.
        equalize()

        // Item (FlowLayout block) content renders asynchronously, so on first
        // page open the records arrive before the inner block content has laid
        // out and the initial measure is too small. Because we use min-height,
        // later content growth makes the grid taller — ResizeObserver fires
        // and check() re-equalizes. With height (the old approach) the grid was
        // pinned and that growth was invisible to the observer.
        const observer = new ResizeObserver(check)
        refs.forEach(ref => observer.observe(ref))

        return () => {
            observer.disconnect()
        }
    }, [uniformRowHeight, squareCells, records, calendarWeeks])

    const renderSingleDayRecords = useCallback(
        (dayRecords: RecordLikeProtocol[], recordIndexPathPrefix: string, dayHasSpanner: boolean) => {
            // Nothing to render: no records, and either a spanner already covers this day
            // or the uniform-height mode is off → skip even the Flex wrapper to avoid
            // rendering an empty div with padding in the DOM.
            if (dayRecords.length === 0 && (dayHasSpanner || !uniformRowHeight)) {
                return null
            }
            return (
                <Flex direction="column" gap={gap} style={{ flex: 1, padding: '2px 8px 6px' }}>
                    {dayRecords.length === 0 ? (
                        // uniformRowHeight is ON and no spanner: render the empty placeholder
                        // with flex:1 so it fills the ContentCell height automatically.
                        <Item
                            style={squareCells ? { aspectRatio: '1 / 1', width: '100%' } : { flex: 1 }}
                            title={title}
                            recordId=""
                            recordIndexPath={`${recordIndexPathPrefix}_empty`}
                            readonly
                            data={{
                                id: `${blockId}/empty_slot/${recordIndexPathPrefix}`,
                                scope: node.scope,
                                type: 'container',
                                virtual: true,
                                disabled: true,
                                data: node.data && {
                                    design: node.data.design,
                                    size: {
                                        width: { size: 'auto' },
                                        height: { size: 'auto' },
                                        ...minHeightConfigure
                                    },
                                    layout: node.data.layout,
                                    position: node.data.position
                                },
                                children: []
                            }}
                        />
                    ) : (
                        dayRecords.map((record, index) => {
                            const isEditable = record.id === editableRecordId
                            return (
                                <Item
                                    key={record.id}
                                    data-record-id={record.id}
                                    recordId={record.id}
                                    recordIndexPath={`${recordIndexPathPrefix}_${index}`}
                                    readonly={readonly}
                                    data={{
                                        id: `${blockId}/${record.id}`,
                                        scope: node.scope,
                                        type: 'container',
                                        disabled: readonly || !isEditable,
                                        virtual: true,
                                        data: node.data && {
                                            design: node.data.design,
                                            size: {
                                                width: { size: 'auto' },
                                                height: { size: 'auto' },
                                                ...minHeightConfigure
                                            },
                                            layout: node.data.layout,
                                            position: node.data.position
                                        },
                                        children: readonly ? node.children : setDeadCopyNode(node.children || [], isEditable, blockId)
                                    }}
                                    title={title}
                                    onRecordClick={onRecordClick}
                                />
                            )
                        })
                    )}
                </Flex>
            )
        },
        [blockId, editableRecordId, gap, minHeightConfigure, node, onRecordClick, readonly, squareCells, title, uniformRowHeight]
    )

    const renderSpannerItem = useCallback(
        (spanner: SpannerSegment, weekIndex: number, segIdx: number) => {
            const isEditable = spanner.record.id === editableRecordId
            return (
                <Item
                    data-record-id={spanner.record.id}
                    recordId={spanner.record.id}
                    recordIndexPath={`span_${weekIndex}_${segIdx}`}
                    readonly={readonly}
                    data={{
                        id: `${blockId}/${spanner.record.id}`,
                        scope: node.scope,
                        type: 'container',
                        disabled: readonly || !isEditable,
                        virtual: true,
                        data: node.data && {
                            design: node.data.design,
                            size: {
                                width: { size: 'auto' },
                                height: { size: 'auto' },
                                ...minHeightConfigure
                            },
                            layout: node.data.layout,
                            position: node.data.position
                        },
                        children: readonly ? node.children : setDeadCopyNode(node.children || [], isEditable, blockId)
                    }}
                    title={title}
                    onRecordClick={onRecordClick}
                />
            )
        },
        [blockId, editableRecordId, minHeightConfigure, node, onRecordClick, readonly, title]
    )

    const borderColor = getBackgroundStyle({ value: viewStyles?.gridColor, palettes }).backgroundColor

    return (
        <Wrapper id={id}>
            <CalendarGrid showBorder={viewStyles?.showGrid ?? true} borderColor={borderColor}>
                {/* Weekday header row */}
                <WeekHeaderRow itemWidth={itemWidth} style={dateRowBackgroundStyle}>
                    {WEEKDAY_LABELS.map((label, i) => (
                        <WeekHeaderCell key={i}>
                            <Text style={getTextColorStyle(viewStyles?.calendarDateRow?.weekColor, palettes)}>{label}</Text>
                        </WeekHeaderCell>
                    ))}
                </WeekHeaderRow>

                {/* One WeekGrid per calendar row */}
                {calendarWeeks.map((week, weekIndex) => {
                    const spanners = weekSpanners[weekIndex]
                    const maxSpannerIndex = spanners.length === 0 ? -1 : Math.max(...spanners.map(s => s.index))
                    // Row 1: date numbers, rows 2..maxSpannerIndex+2: spanners, row maxSpannerIndex+3: single-day content
                    const contentRow = maxSpannerIndex + 3

                    // Compute which columns are occupied by spanners in each spanner row
                    const rowCoverage = new Map<number, Set<number>>()
                    for (let rowOffset = 0; rowOffset <= maxSpannerIndex; rowOffset++) {
                        const covered = new Set<number>()
                        spanners
                            .filter(s => s.index === rowOffset)
                            .forEach(s => {
                                for (let c = s.startCol; c < s.startCol + s.colSpan; c++) {
                                    covered.add(c)
                                }
                            })
                        rowCoverage.set(rowOffset, covered)
                    }

                    // Which columns are touched by ANY spanner (regardless of row)?
                    // Columns NOT in this set will have their ContentCell span from row 2,
                    // so their single-day content sits right below the date header instead
                    // of being pushed down to contentRow by the empty spanner rows above.
                    const colHasSpanner = new Set<number>()
                    // Per-column max spanner row index. Each column's single-day content
                    // must sit right after THAT column's last spanner — not the whole week's —
                    // otherwise a column whose spanner only occupies row 2 gets pushed down
                    // to the global contentRow, opening an empty "phantom" row in between.
                    const colMaxSpannerIndex = new Map<number, number>()
                    for (const spanner of spanners) {
                        for (let c = spanner.startCol; c < spanner.startCol + spanner.colSpan; c++) {
                            colHasSpanner.add(c)
                            colMaxSpannerIndex.set(c, Math.max(colMaxSpannerIndex.get(c) ?? -1, spanner.index))
                        }
                    }

                    return (
                        <WeekGrid
                            ref={(el: HTMLDivElement | null) => { weekGridRefs.current[weekIndex] = el }}
                            key={week[0].getTime()}
                            itemWidth={itemWidth}
                            uniformRowHeight={uniformRowHeight}
                            squareCells={squareCells}
                            contentRow={contentRow}
                        >
                            {/* Row 1: date number cells */}
                            {week.map((day, dayIndex) => (
                                <DateCell
                                    key={day.getTime()}
                                    style={{ gridColumn: dayIndex + 1, gridRow: 1, ...dateRowBackgroundStyle }}
                                >
                                    <DayNumber
                                        align="center"
                                        isCurrentMonth={isSameMonth(day, currentMonth)}
                                        style={getTextColorStyle(viewStyles?.calendarDateRow?.dateColor, palettes)}
                                    >
                                        {lightFormat(day, 'd')}
                                    </DayNumber>
                                </DateCell>
                            ))}

                            {/*
                             * Ghost border cells: for each spanner row, fill every column NOT covered by a
                             * SpannerCell with an empty div that has border-right.
                             * CSS Grid stretches these elements to the row height (align-self: stretch is the default),
                             * keeping column separators visually continuous with the rows above and below.
                             */}
                            {maxSpannerIndex >= 0 &&
                                Array.from({ length: maxSpannerIndex + 1 }).flatMap((_, rowOffset) => {
                                    const covered = rowCoverage.get(rowOffset) ?? new Set<number>()
                                    return Array.from({ length: 7 }, (_, colIdx) => {
                                        const col = colIdx + 1
                                        if (covered.has(col)) return null
                                        // Columns with no spanner at all use a spanning ContentCell
                                        // that already covers all rows from 2 to contentRow —
                                        // no ghost cell needed for those columns.
                                        if (!colHasSpanner.has(col)) return null
                                        // This column's content cell already spans down from its
                                        // own last spanner row (colMaxIdx + 3) to contentRow, so it
                                        // covers this row and provides the border — no ghost needed.
                                        const colMaxIdx = colMaxSpannerIndex.get(col)!
                                        if (rowOffset > colMaxIdx) return null
                                        return (
                                            <SpannerBorderCell
                                                key={`ghost-${rowOffset}-${col}`}
                                                style={{ gridColumn: col, gridRow: rowOffset + 2 }}
                                            />
                                        )
                                    })
                                })}

                            {/* Spanning record rows (gridRow: index + 2) */}
                            {spanners.map((spanner, segIdx) => (
                                <SpannerCell
                                    key={`${spanner.record.id}-${weekIndex}-${segIdx}`}
                                    style={{
                                        gridColumn: `${spanner.startCol} / span ${spanner.colSpan}`,
                                        gridRow: spanner.index + 2
                                    }}
                                >
                                    {renderSpannerItem(spanner, weekIndex, segIdx)}
                                </SpannerCell>
                            ))}

                            {/* Single-day record content cells */}
                            {week.map((day, dayIndex) => {
                                const col = dayIndex + 1
                                const dayRecords = getSingleDayRecords(day)
                                // No spanner touches this column → content fills from row 2.
                                // A spanner touches it → content starts right after THIS column's
                                // last spanner (colMaxIdx + 3) and spans down to contentRow, so no
                                // empty gap opens below it even when another column has more spanner
                                // rows.
                                const colMaxIdx = colMaxSpannerIndex.get(col)
                                const contentStartRow = colMaxIdx === undefined ? 2 : colMaxIdx + 3
                                return (
                                    <ContentCell
                                        key={day.getTime()}
                                        style={{ gridColumn: col, gridRow: `${contentStartRow} / ${contentRow + 1}` }}
                                    >
                                        {renderSingleDayRecords(dayRecords, `${weekIndex}_${dayIndex}`, colHasSpanner.has(col))}
                                    </ContentCell>
                                )
                            })}
                        </WeekGrid>
                    )
                })}
            </CalendarGrid>
        </Wrapper>
    )
})
